"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getFederalOnSiteCap = getFederalOnSiteCap;
exports.getOnSiteCap = getOnSiteCap;
const wasteGeneratorStatusThresholds_1 = require("encamp-shared/dist/constants/wasteGeneratorStatusThresholds");
const facilityState_1 = require("../facilityState");
const MN_1 = require("./jurisdictions/MN");
const UNCAPPED = { totalKg: null, nonAcuteKg: null, acuteKg: null };
/**
 * Federal on-site quantity caps (40 CFR 262.14/262.16): VSQGs are capped per
 * classification (1,000 kg non-acute AND 1 kg acute), SQGs by a single
 * 6,000 kg total, and LQGs not at all (time-limited instead).
 */
function getFederalOnSiteCap(generatorStatus) {
    switch (generatorStatus) {
        case "VSQG":
            return { totalKg: null, nonAcuteKg: wasteGeneratorStatusThresholds_1.VSQG_ON_SITE_CAP_KG, acuteKg: wasteGeneratorStatusThresholds_1.VSQG_ON_SITE_ACUTE_CAP_KG };
        case "SQG":
            return { totalKg: wasteGeneratorStatusThresholds_1.SQG_ON_SITE_CAP_KG, nonAcuteKg: null, acuteKg: null };
        // LQGs have no quantity cap; an unresolvable status gets no cap either —
        // the caller prompts for setup rather than assuming a status.
        default:
            return UNCAPPED;
    }
}
/**
 * Minnesota's caps (Minn. R. 7045.0292), keyed on the EFFECTIVE Minnesota
 * tier — the same stricter-of-both-axes derivation the MN clocks use, MiniQG
 * folding into VSQG. Two departures from federal worth naming:
 *
 * - The SQG cap is 3,000 kg (subps. 5, 11) — HALF the federal 6,000 kg — and
 *   it is textually a NON-ACUTE cap ("hazardous waste that is not acute
 *   hazardous waste"), not a total, so it rides the nonAcuteKg field. For the
 *   specialist to confirm alongside the layer-2 satellite readings.
 * - Subp. 7 caps acute accumulation at 1 kg for SQGs AND VSQGs (its 100 kg
 *   contaminated-residue companion limit is unmodeled, the same way federal
 *   262.14's residue conditions are).
 *
 * A half-known status pair derives a null tier (can't rule out LQG), which
 * resolves UNCAPPED — the same "caller prompts for setup" convention the
 * federal null takes, and the same posture as MN's UNDETERMINED clocks.
 */
function getMinnesotaOnSiteCap(effectiveStatus) {
    switch (effectiveStatus) {
        case "VSQG":
            return {
                totalKg: null,
                nonAcuteKg: wasteGeneratorStatusThresholds_1.MN_VSQG_ON_SITE_CAP_KG,
                acuteKg: wasteGeneratorStatusThresholds_1.MN_ON_SITE_ACUTE_CAP_KG,
            };
        case "SQG":
            return { totalKg: null, nonAcuteKg: wasteGeneratorStatusThresholds_1.MN_SQG_ON_SITE_CAP_KG, acuteKg: wasteGeneratorStatusThresholds_1.MN_ON_SITE_ACUTE_CAP_KG };
        default:
            return UNCAPPED;
    }
}
/**
 * Connecticut's caps (RCSA § 22a-449(c)-101, -102 incorporating 40 CFR 261.5
 * and 262.34(d) as of July 1, 2000), keyed on the FEDERAL tier — Connecticut
 * defines no tier of its own and RCRAInfo carries none for it, so the CT
 * clocks read the same axis. One departure from federal: the SQG cap is
 * 1,000 kg, a SIXTH of the federal 6,000 kg, and textually a total the way
 * the federal one is, so it rides `totalKg`. The smallest tier — the federal
 * VSQG, which Connecticut still calls the CESQG — keeps 261.5's 1,000 kg
 * non-acute / 1 kg acute, the same numbers the federal VSQG cap carries. Both
 * await the SME gate.
 */
function getConnecticutOnSiteCap(generatorStatus) {
    switch (generatorStatus) {
        case "VSQG":
            return { totalKg: null, nonAcuteKg: wasteGeneratorStatusThresholds_1.VSQG_ON_SITE_CAP_KG, acuteKg: wasteGeneratorStatusThresholds_1.VSQG_ON_SITE_ACUTE_CAP_KG };
        case "SQG":
            return { totalKg: wasteGeneratorStatusThresholds_1.CT_SQG_ON_SITE_CAP_KG, nonAcuteKg: null, acuteKg: null };
        default:
            return UNCAPPED;
    }
}
/**
 * The on-site cap for a facility, chosen by the state it sits in. Shaped like
 * `getEpisodicRelief`: a per-state fact resolved from the raw facility state,
 * defaulting federal for every state without caps of its own.
 */
function getOnSiteCap(query) {
    switch ((0, facilityState_1.parseFacilityState)(query.state)) {
        case "CT":
            return {
                cap: getConnecticutOnSiteCap(query.generatorStatus),
                capGeneratorStatus: query.generatorStatus,
            };
        case "MN": {
            const effectiveStatus = (0, MN_1.effectiveMinnesotaStatus)(query);
            return { cap: getMinnesotaOnSiteCap(effectiveStatus), capGeneratorStatus: effectiveStatus };
        }
        default:
            return {
                cap: getFederalOnSiteCap(query.generatorStatus),
                capGeneratorStatus: query.generatorStatus,
            };
    }
}
//# sourceMappingURL=onSiteCaps.js.map